import React, { useEffect, useState } from "react";
import { trackHome } from "@/lib/requestContext";
import { Link } from "react-router-dom";
import { Search, Tag } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import api from "@/lib/api";
import DealCard, { DEAL_STYLES } from "@/components/deals/DealCard";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const KINDS = [["", "All deals"], ["local", "Local"], ["digital", "Digital"]];
const SORTS = [["ending", "Ending soon"], ["discount", "Biggest discount"], ["price", "Lowest price"], ["newest", "Newest"]];

export default function DealsPage() {
  useEffect(() => { trackHome("deal_view"); }, []);
  const [kind, setKind] = useState("");
  const [sort, setSort] = useState("ending");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  useDocumentTitle("Deals");
  useEffect(() => {
    const t = setTimeout(() => api.get("/deals", { params: { kind: kind || undefined, sort, q: q || undefined } }).then((r) => setItems(r.data.items)).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [kind, sort, q]);

  return (
    <PublicLayout>
      <style>{DEAL_STYLES}</style>
      <section className="bg-[#071b22] text-white">
        <div className="mx-auto max-w-7xl px-6 py-14 md:py-20 grid gap-8 md:grid-cols-[1.4fr_1fr] items-end">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#d97757] px-3 py-1 text-xs font-bold uppercase tracking-[0.18em]"><Tag className="h-3.5 w-3.5" /> Limited-time deals</div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02]">Local pros. Digital talent.<br /><span className="text-[#f2b38f]">Better prices.</span></h1>
            <p className="mt-4 text-white/70 max-w-xl">Real discounts from verified Gulf Coast Work providers. Limited quantities — the discount is applied automatically when you book.</p>
          </div>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deals, services or pros" className="w-full rounded-full bg-white/10 border border-white/20 pl-11 pr-4 py-3.5 text-sm outline-none placeholder:text-white/45 focus:bg-white/15 focus:border-white/40" data-testid="deals-search" />
          </div>
        </div>
      </section>
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-2">
            {KINDS.map(([k, l]) => <button key={l} onClick={() => setKind(k)} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${kind === k ? "bg-[#071b22] text-white" : "bg-white border border-slate-200 text-[#252A34]/80 hover:border-[#165a71]/40"}`} data-testid={`deals-kind-${k || "all"}`}>{l}</button>)}
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm" data-testid="deals-sort">
            {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        {items === null ? <div className="mt-10 text-[#252A34]/60">Loading deals…</div> : items.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="deals-empty">
            <Tag className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg font-semibold text-[#071b22]">No deals right now</div>
            <div className="text-sm text-[#252A34]/65 mt-1">Check back soon — or <Link to="/request-a-service" className="text-[#165a71] underline">request a service</Link> and get estimates.</div>
          </div>
        ) : (
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="deals-grid">
            {items.map((d, i) => <DealCard key={d.id} d={d} i={i} />)}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
